import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getCroppedImg } from '@/lib/cropImage'

interface MemberPhotoCropModalProps {
  open: boolean
  onClose: () => void
  /**
   * Called when the user confirms their crops. Either blob may be null if the user chose
   * to save only one format. The parent is responsible for uploading the provided blobs.
   */
  onSave: (squareBlob: Blob | null, portraitBlob: Blob | null) => Promise<void>
}

export function MemberPhotoCropModal({ open, onClose, onSave }: MemberPhotoCropModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)

  const [squareCrop, setSquareCrop] = useState({ x: 0, y: 0 })
  const [squareZoom, setSquareZoom] = useState(1)
  const [squareCroppedAreaPixels, setSquareCroppedAreaPixels] = useState<Area | null>(null)

  const [portraitCrop, setPortraitCrop] = useState({ x: 0, y: 0 })
  const [portraitZoom, setPortraitZoom] = useState(1)
  const [portraitCroppedAreaPixels, setPortraitCroppedAreaPixels] = useState<Area | null>(null)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Reset all state and revoke the object URL when the modal closes
  useEffect(() => {
    if (!open) {
      if (imageSrc) URL.revokeObjectURL(imageSrc)
      setImageSrc(null)
      setSquareCrop({ x: 0, y: 0 })
      setSquareZoom(1)
      setSquareCroppedAreaPixels(null)
      setPortraitCrop({ x: 0, y: 0 })
      setPortraitZoom(1)
      setPortraitCroppedAreaPixels(null)
      setSaving(false)
      setSaveError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (imageSrc) URL.revokeObjectURL(imageSrc)
    const url = URL.createObjectURL(file)
    setImageSrc(url)
    setSquareCrop({ x: 0, y: 0 })
    setSquareZoom(1)
    setSquareCroppedAreaPixels(null)
    setPortraitCrop({ x: 0, y: 0 })
    setPortraitZoom(1)
    setPortraitCroppedAreaPixels(null)
    setSaveError(null)
    // Allow re-selecting the same file
    e.target.value = ''
  }

  async function handleSaveBoth() {
    if (!imageSrc || !squareCroppedAreaPixels || !portraitCroppedAreaPixels) return
    setSaving(true)
    setSaveError(null)
    try {
      const [squareBlob, portraitBlob] = await Promise.all([
        getCroppedImg(imageSrc, squareCroppedAreaPixels, 500, 500),
        getCroppedImg(imageSrc, portraitCroppedAreaPixels, 1080, 1920),
      ])
      await onSave(squareBlob, portraitBlob)
    } catch (err) {
      setSaveError(formatUserFacingError(err, 'Failed to process images.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveSquareOnly() {
    if (!imageSrc || !squareCroppedAreaPixels) return
    setSaving(true)
    setSaveError(null)
    try {
      const squareBlob = await getCroppedImg(imageSrc, squareCroppedAreaPixels, 500, 500)
      await onSave(squareBlob, null)
    } catch (err) {
      setSaveError(formatUserFacingError(err, 'Failed to process square image.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleSavePortraitOnly() {
    if (!imageSrc || !portraitCroppedAreaPixels) return
    setSaving(true)
    setSaveError(null)
    try {
      const portraitBlob = await getCroppedImg(imageSrc, portraitCroppedAreaPixels, 1080, 1920)
      await onSave(null, portraitBlob)
    } catch (err) {
      setSaveError(formatUserFacingError(err, 'Failed to process portrait image.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload &amp; Crop Photos</DialogTitle>
          <DialogDescription>
            Select one image to use for both crops. Adjust each crop independently, then save.
          </DialogDescription>
        </DialogHeader>

        {/* File selection */}
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
          >
            {imageSrc ? 'Choose a Different Image' : 'Select Image'}
          </Button>
          {!imageSrc && (
            <p className="text-xs text-muted-foreground">
              Choose a high-resolution image. The same photo will be used for both crops.
            </p>
          )}
        </div>

        {/* Two-column crop area */}
        {imageSrc && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">

            {/* Square crop column */}
            <div className="space-y-3">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Square Photo</p>
                <p className="text-xs text-muted-foreground">500 × 500 px — circular avatar</p>
              </div>
              {/* Cropper MUST have position:relative and explicit height */}
              <div className="relative h-64 w-full overflow-hidden rounded-md border bg-muted">
                <Cropper
                  image={imageSrc}
                  crop={squareCrop}
                  zoom={squareZoom}
                  aspect={1}
                  objectFit="contain"
                  onCropChange={setSquareCrop}
                  onZoomChange={setSquareZoom}
                  onCropComplete={(_, croppedAreaPixels) =>
                    setSquareCroppedAreaPixels(croppedAreaPixels)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Zoom</Label>
                <Slider
                  min={1}
                  max={3}
                  step={0.01}
                  value={[squareZoom]}
                  onValueChange={(vals) => setSquareZoom(vals[0] ?? 1)}
                  disabled={saving}
                />
              </div>
            </div>

            {/* Portrait crop column */}
            <div className="space-y-3">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Portrait Photo</p>
                <p className="text-xs text-muted-foreground">1080 × 1920 px — member page</p>
              </div>
              {/* Cropper MUST have position:relative and explicit height */}
              <div className="relative h-64 w-full overflow-hidden rounded-md border bg-muted">
                <Cropper
                  image={imageSrc}
                  crop={portraitCrop}
                  zoom={portraitZoom}
                  aspect={9 / 16}
                  objectFit="contain"
                  onCropChange={setPortraitCrop}
                  onZoomChange={setPortraitZoom}
                  onCropComplete={(_, croppedAreaPixels) =>
                    setPortraitCroppedAreaPixels(croppedAreaPixels)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Zoom</Label>
                <Slider
                  min={1}
                  max={3}
                  step={0.01}
                  value={[portraitZoom]}
                  onValueChange={(vals) => setPortraitZoom(vals[0] ?? 1)}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        )}

        {saveError && (
          <p className="text-sm text-destructive">{saveError}</p>
        )}

        {/* Footer actions */}
        {imageSrc && (
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saving || !squareCroppedAreaPixels}
              onClick={() => void handleSaveSquareOnly()}
            >
              {saving ? '…' : 'Save Square Only'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saving || !portraitCroppedAreaPixels}
              onClick={() => void handleSavePortraitOnly()}
            >
              {saving ? '…' : 'Save Portrait Only'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={saving || !squareCroppedAreaPixels || !portraitCroppedAreaPixels}
              onClick={() => void handleSaveBoth()}
            >
              {saving ? 'Processing…' : 'Save Both Photos'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
